import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, Wifi, WifiOff, BatteryMedium, Truck, Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";

const KEY = "drv_app_token";
const PING_MS = 30000;

async function call(path, token, opts = {}) {
  const r = await fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...(token ? { "X-Driver-Token": token } : {}), ...(opts.headers || {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || "Request failed");
  return j;
}

function AppLogin({ onToken }) {
  const [id, setId] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault(); setErr("");
    try { const j = await call("/v1/driver/app-login", null, { method: "POST", body: JSON.stringify({ login_id: id, pin }) }); onToken(j.token); } catch (x) { setErr(x.message); }
  };
  return (
    <form onSubmit={go} className="space-y-3" data-testid="drv-app-login">
      <Input placeholder="Login ID (DRV123456)" className="h-12 uppercase" data-testid="drv-login-id" value={id} onChange={(e) => setId(e.target.value)} />
      <Input placeholder="Passcode" inputMode="numeric" className="h-12" data-testid="drv-login-pin" value={pin} onChange={(e) => setPin(e.target.value)} />
      {err && <p className="text-sm text-rose-600" data-testid="drv-login-error">{err}</p>}
      <Button className="h-12 w-full text-base" data-testid="drv-login-btn">Login</Button>
    </form>
  );
}

export default function DriverTrackApp() {
  const { token: urlToken } = useParams();
  const nav = useNavigate();
  const [token, setToken] = useState(() => urlToken || localStorage.getItem(KEY) || "");
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [on, setOn] = useState(false);
  const [st, setSt] = useState({ last: null, gps: null, sent: 0, net: true, acc: null });
  const pos = useRef(null);
  const gpsErr = useRef(false);
  const watch = useRef(null);
  const timer = useRef(null);
  const lock = useRef(null);

  useEffect(() => {
    if (!token) return;
    localStorage.setItem(KEY, token);
    if (urlToken) nav("/track", { replace: true });
    call("/v1/driver/me", token).then(setMe).catch((e) => { setErr(e.message); localStorage.removeItem(KEY); setToken(""); });
  }, [token, urlToken, nav]);

  const send = useCallback(async () => {
    let battery = null;
    try { if (navigator.getBattery) battery = Math.round((await navigator.getBattery()).level * 100); } catch { battery = null; }
    const c = pos.current;
    const body = c && !gpsErr.current
      ? { lat: c.latitude, lng: c.longitude, speed: c.speed, accuracy: c.accuracy, battery, timestamp: new Date().toISOString(), gps_on: true }
      : { gps_on: false, battery, timestamp: new Date().toISOString() };
    try {
      const j = await call("/v1/driver/location-ping", token, { method: "POST", body: JSON.stringify(body) });
      setSt((s) => ({ ...s, last: new Date(), sent: s.sent + 1, net: true, gps: body.gps_on, acc: c?.accuracy }));
      if (j.tracking_enabled === false) setMe((m) => ({ ...m, tracking_enabled: false }));
    } catch { setSt((s) => ({ ...s, net: false })); }
  }, [token]);

  const start = async () => {
    if (!navigator.geolocation) { setErr("Is phone me GPS support nahi hai"); return; }
    watch.current = navigator.geolocation.watchPosition(
      (p) => { pos.current = p.coords; gpsErr.current = false; },
      () => { gpsErr.current = true; setSt((s) => ({ ...s, gps: false })); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 });
    try { lock.current = await navigator.wakeLock?.request("screen"); } catch { lock.current = null; }
    setOn(true);
    setTimeout(send, 4000);
    timer.current = setInterval(send, PING_MS);
  };
  const stop = () => {
    if (watch.current != null) navigator.geolocation.clearWatch(watch.current);
    clearInterval(timer.current);
    lock.current?.release?.();
    setOn(false);
  };
  useEffect(() => () => { clearInterval(timer.current); if (watch.current != null) navigator.geolocation.clearWatch(watch.current); }, []);

  return (
    <div className="mx-auto min-h-screen max-w-md bg-background p-5" data-testid="driver-track-app">
      <div className="mb-6 flex items-center gap-2"><Truck className="h-6 w-6 text-primary" /><div><div className="font-bold">Driver Tracking</div>
        <div className="text-xs text-muted-foreground">{me?.company || "TransLogiX"}</div></div></div>
      {!token || !me ? (
        <>{err && <p className="mb-3 rounded bg-rose-50 p-2 text-sm text-rose-700">{err}</p>}{!token ? <AppLogin onToken={(t) => { setErr(""); setToken(t); }} /> : <p>Loading…</p>}</>
      ) : (
        <div className="space-y-4">
          <div className="card-base p-4" data-testid="drv-app-info">
            <div className="text-lg font-bold">{me.name}</div>
            <div className="text-sm text-muted-foreground">{me.vehicle_number || "No vehicle"}</div>
            <div className="mt-2 text-sm">{me.trip ? <>Trip <b>{me.trip.lr_number}</b> · {me.trip.route}</> : "Abhi koi active trip nahi"}</div>
          </div>
          {me.tracking_enabled === false ? (
            <p className="rounded bg-amber-50 p-3 text-sm font-semibold text-amber-800" data-testid="drv-tracking-off">Office ne aapki tracking band ki hai.</p>
          ) : on ? (
            <Button variant="destructive" className="h-14 w-full text-base" data-testid="drv-stop-btn" onClick={stop}><Square className="mr-2 h-5 w-5" />Stop Tracking</Button>
          ) : (
            <Button className="h-14 w-full text-base" data-testid="drv-start-btn" onClick={start}><Play className="mr-2 h-5 w-5" />Start Tracking</Button>
          )}
          <div className="grid grid-cols-3 gap-2 text-center text-xs" data-testid="drv-app-status">
            <div className="card-base p-3"><MapPin className={`mx-auto h-5 w-5 ${st.gps === false ? "text-rose-600" : "text-emerald-600"}`} />{st.gps === false ? "GPS OFF" : st.gps ? `GPS ${Math.round(st.acc || 0)}m` : "GPS —"}</div>
            <div className="card-base p-3">{st.net ? <Wifi className="mx-auto h-5 w-5 text-emerald-600" /> : <WifiOff className="mx-auto h-5 w-5 text-rose-600" />}{st.net ? "Online" : "No internet"}</div>
            <div className="card-base p-3"><BatteryMedium className="mx-auto h-5 w-5" />{st.sent} pings</div>
          </div>
          {on && <p className="text-center text-xs text-muted-foreground" data-testid="drv-last-sent">Last sent: {st.last ? st.last.toLocaleTimeString("en-IN") : "…"} · har 30 sec. Ye screen khuli rakhein, phone lock na karein.</p>}
          <button className="w-full text-center text-xs text-muted-foreground underline" data-testid="drv-logout" onClick={() => { stop(); localStorage.removeItem(KEY); setToken(""); setMe(null); }}>Logout</button>
        </div>
      )}
    </div>
  );
}
